import type { ReactNode } from "react";
import "../../styles/components/panel.css";

export interface PanelProps {
  title?: ReactNode;
  /** 标题下的一句说明（补充口径/时间范围等），不宜超过一行 */
  desc?: ReactNode;
  /** 标题右侧的操作区（按钮/分段控件/图例） */
  extra?: ReactNode;
  /** 去掉内边距，用于内部自带贴边表格/列表的场景 */
  flush?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * 内容面板：页面信息架构的基本单元（页头 → 指标卡 → 主内容面板 → 操作区）。
 * 视觉：白底 + 14px 圆角 + 1px 分隔线 + 柔和投影，与参考工程一致。
 */
export function Panel({ title, desc, extra, flush = false, className = "", children }: PanelProps) {
  return (
    <section className={`panel${flush ? " panel-flush" : ""}${className ? " " + className : ""}`}>
      {(title || extra) && (
        <header className="panel-head">
          <div className="panel-head-main">
            {title ? <h2 className="panel-title">{title}</h2> : null}
            {desc ? <p className="panel-desc">{desc}</p> : null}
          </div>
          {extra ? <div className="panel-extra">{extra}</div> : null}
        </header>
      )}
      <div className="panel-body">{children}</div>
    </section>
  );
}

export interface PageHeadProps {
  title: string;
  desc?: ReactNode;
  /** 右侧操作区 */
  extra?: ReactNode;
}

/**
 * 页面头部：标题 + 说明 + 右侧操作，全站页面统一入口结构。
 *
 * 2026-09-23 导航重构：**移除 eyebrow（模块归属小字）** —— 归属信息已由顶部业务域页签/侧栏表达，
 * 保留它会让同一模块名在一屏内重复出现（此前「库存管理」出现过 4 次）。
 */
export function PageHead({ title, desc, extra }: PageHeadProps) {
  return (
    <div className="page-head">
      <div className="page-head-main">
        <h1 className="page-title">{title}</h1>
        {desc ? <p className="page-desc">{desc}</p> : null}
      </div>
      {extra ? <div className="page-head-extra">{extra}</div> : null}
    </div>
  );
}

export default Panel;
